import React from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrums";
import GuideImageMedia from "../components/GuideImageMedia";
import { GuidesContent, getGuideBySlug } from "../configs/guides";

function GuideDetails() {
  const { slug } = useParams();
  const guide = getGuideBySlug(slug);

  if (!guide) {
    return (
      <div className="flex flex-col justify-center lg:p-3">
        <Breadcrumbs
          items={[
            { label: "Home", path: "/" },
            { label: "Guides", path: "/guides" },
            { label: "Not Found", path: "/guides" },
          ]}
        />
        <div className="px-3 pb-4">
          <div className="bg-primary-main rounded-lg shadow-md p-4 border border-secondary-medium/30">
            <h1 className="text-xl font-bold text-secondary-main">
              Guide not found
            </h1>
            <p className="text-sm text-secondary-dark mt-2">
              This guide may have moved. Browse all available guides below.
            </p>
            <Link
              to="/guides"
              className="mt-3 inline-flex px-3 py-2 rounded-md text-sm font-semibold bg-secondary-main text-primary-main hover:bg-secondary-dark transition-colors"
            >
              Back to Guides
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: "Guides", path: "/guides" },
    { label: guide.title, path: `/guides/${guide.slug}` },
  ];

  return (
    <div className="flex flex-col justify-center lg:p-3">
      <Breadcrumbs items={breadcrumbItems} />

      <div className="px-3 pb-4">
        <section className="bg-primary-main rounded-lg shadow-md p-4 border border-secondary-medium/30 mb-4">
          <div className="relative aspect-[4/5] w-full max-h-[36rem] rounded-lg overflow-hidden mb-4 border border-black/10 bg-secondary-dark/10">
            <GuideImageMedia
              src={guide.coverImage}
              alt={guide.title}
              imageClassName="p-3 sm:p-4"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
            <div className="absolute bottom-3 left-3 right-3">
              <h1 className="text-2xl md:text-3xl font-bold text-brand-light leading-tight">
                {guide.title}
              </h1>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs uppercase tracking-wide text-secondary-dark font-semibold">
              {guide.badge}
            </p>
            <p className="text-xs text-secondary-dark">
              {guide.readingMinutes} min read
            </p>
          </div>

          <p className="text-sm text-secondary-dark mt-2 leading-relaxed">
            {guide.summary}
          </p>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="rounded-md bg-secondary-medium/10 border border-secondary-medium/20 p-3">
              <p className="text-xs uppercase tracking-wide text-secondary-dark">
                Start Point
              </p>
              <p className="text-sm font-semibold text-secondary-main mt-1">
                {guide.startFrom}
              </p>
            </div>
            <div className="rounded-md bg-secondary-medium/10 border border-secondary-medium/20 p-3">
              <p className="text-xs uppercase tracking-wide text-secondary-dark">
                Best For
              </p>
              <p className="text-sm font-semibold text-secondary-main mt-1">
                {guide.bestFor}
              </p>
            </div>
            <div className="rounded-md bg-secondary-medium/10 border border-secondary-medium/20 p-3">
              <p className="text-xs uppercase tracking-wide text-secondary-dark">
                Travel Style
              </p>
              <p className="text-sm font-semibold text-secondary-main mt-1">
                {guide.vibe}
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {guide.highlights.map((highlight) => (
              <span
                key={highlight}
                className="text-xs px-2 py-1 rounded-full bg-secondary-dark/12 text-secondary-dark"
              >
                {highlight}
              </span>
            ))}
          </div>
        </section>

        {guide.itinerary && (
          <section className="mb-4 rounded-lg border border-secondary-medium/20 bg-primary-main/80 p-3 sm:p-4">
            <h2 className="text-lg font-semibold text-secondary-main mb-2">
              Itinerary
            </h2>
            <p className="text-sm font-medium text-secondary-medium mb-3 leading-relaxed">
              Follow this route as-is, or swap stops based on weather, energy,
              and opening hours.
            </p>
            <div className="flex flex-col gap-3">
              {guide.itinerary.map((stop, index) => (
                <article
                  key={`${stop.time}-${index}`}
                  className="bg-primary-main rounded-lg shadow-sm border border-secondary-medium/25 p-4 border-l-4 border-l-secondary-main"
                >
                  <p className="text-xs uppercase tracking-wide text-secondary-dark font-semibold">
                    {stop.time}
                  </p>
                  <h3 className="text-base font-semibold text-secondary-main mt-1">
                    {stop.title}
                  </h3>
                  <p className="text-sm text-secondary-dark mt-2 leading-relaxed">
                    {stop.details}
                  </p>
                  {stop.duration && (
                    <p className="text-xs text-secondary-dark mt-2">
                      Suggested duration: {stop.duration}
                    </p>
                  )}
                  {stop.travelTip && (
                    <p className="text-xs text-secondary-dark mt-1">
                      Travel tip: {stop.travelTip}
                    </p>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}

        {guide.sections && (
          <section className="mb-4 rounded-lg border border-secondary-medium/20 bg-primary-main/80 p-3 sm:p-4">
            <h2 className="text-lg font-semibold text-secondary-main mb-2">
              Activities
            </h2>
            <p className="text-sm font-medium text-secondary-medium mb-3 leading-relaxed">
              Use these lists like a menu. Pick based on your available time,
              interests, and travel pace.
            </p>
            <div className="flex flex-col gap-3">
              {guide.sections.map((section) => (
                <article
                  key={section.title}
                  className="bg-primary-main rounded-lg shadow-sm border border-secondary-medium/25 p-4"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h3 className="text-base font-semibold text-secondary-main">
                      {section.title}
                    </h3>
                    <span className="text-xs px-2 py-1 rounded-full bg-secondary-medium/20 text-secondary-main">
                      {section.items.length} ideas
                    </span>
                  </div>
                  {section.intro && (
                    <p className="text-sm text-secondary-dark mt-1 leading-relaxed">
                      {section.intro}
                    </p>
                  )}
                  <ol className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {section.items.map((item) => (
                      <li
                        key={item}
                        className="text-sm text-secondary-dark list-decimal ml-4"
                      >
                        {item}
                      </li>
                    ))}
                  </ol>
                </article>
              ))}
            </div>
          </section>
        )}

        <section className="bg-primary-main rounded-lg shadow-md p-4 border border-secondary-medium/30">
          <h2 className="text-base font-semibold text-secondary-main">
            Explore Other Guides
          </h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {GuidesContent.filter((item) => item.slug !== guide.slug).map(
              (item) => (
                <Link
                  key={item.slug}
                  to={`/guides/${item.slug}`}
                  className="px-3 py-2 rounded-md text-sm bg-secondary-main text-primary-main hover:bg-secondary-dark transition-colors"
                >
                  {item.badge}
                </Link>
              ),
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default GuideDetails;
